"use client";

import { useDeferredValue, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  Panel,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import type { JsonIndent } from "@/lib/tool-kit/latex-dev/data-model";
import {
  type ConvertOptions,
  DEFAULT_CONVERT_OPTIONS,
  type Direction,
  jsonToYaml,
  type Outcome,
  type Problem,
  yamlToJson,
} from "@/lib/tool-kit/latex-dev/yaml-json";

/** English strings. A translated content file overrides these through `ui`. */
export const YAML_JSON_CONVERTER_STRINGS = {
  modeLabel: "Direction",
  modes: {
    yamlToJson: "YAML → JSON",
    jsonToYaml: "JSON → YAML",
  },
  inputLabelYaml: "YAML to convert",
  inputLabelJson: "JSON to convert",
  placeholderYaml: "name: web\nreplicas: 3\ntags: [api, internal]",
  placeholderJson: '{"name": "web", "replicas": 3}',
  hint: "Converted in your browser as you type. Nothing is uploaded.",
  loadExample: "Load the example",
  clear: "Clear",
  useAsInput: "Use the result as input and swap direction",
  exampleYaml:
    '# Service configuration\nname: web\nreplicas: 3\nenabled: true\nratio: 0.75\ntags: [api, internal]\nenv:\n  LOG_LEVEL: info\n  TIMEOUT: "30"\nports:\n  - 80\n  - 443\ndescription: >\n  A long description that is\n  folded into one line.\ndefaults: &defaults\n  retries: 3\n  region: eu\nstaging:\n  <<: *defaults\n  region: us\n',
  exampleJson:
    '{"name": "web", "replicas": 3, "tags": ["api", "internal"], "env": {"LOG_LEVEL": "info", "TIMEOUT": "30", "DEBUG": false}, "notes": "line one\\nline two\\n", "version": "1.10", "country": "NO", "owner": null, "ports": [80, 443], "empty": {}, "list": []}',
  optionsHeading: "Options",
  jsonIndentLabel: "JSON indentation",
  jsonIndents: {
    "2": "2 spaces",
    "4": "4 spaces",
    tab: "Tab",
    compact: "Minified (one line)",
  },
  yamlIndentLabel: "YAML indentation",
  yamlIndents: {
    "2": "2 spaces",
    "4": "4 spaces",
  },
  blockScalars: "Write multi-line text as | blocks",
  merge: "Apply << merge keys (copy the anchored mapping in)",
  results: "Result",
  outputLabelJson: "JSON",
  outputLabelYaml: "YAML",
  copyResult: "Copy result",
  copied: "Copied!",
  download: "Download file",
  filenameJson: "converted.json",
  filenameYaml: "converted.yaml",
  emptyPrompt: "Paste some YAML or JSON, or load the example, to convert it.",
  valid: "Valid and converted.",
  emptyDocument:
    "The YAML holds only comments or white space, so there is nothing to convert.",
  documentsNote:
    "The input has {count} YAML documents, so the JSON is an array with one item per document.",
  typeLabel: "Top level",
  types: {
    mapping: "Mapping",
    sequence: "List",
    scalar: "Single value",
  },
  entries: "Top-level entries",
  depth: "Levels deep",
  keys: "Keys in total",
  warningsHeading: "Worth a second look",
  problemHeading: "Could not convert",
  problemAt: "Line {line}, column {column}",
  problemNoLine: "The input could not be converted",
  messages: {
    tabIndent: "A tab is used for indentation. YAML allows only spaces there.",
    badIndent:
      "This line is indented differently from the lines it should line up with.",
    noColon:
      "Expected “key: value” here, but there is no colon followed by a space.",
    mappingValues:
      "A “key: value” is not allowed here. Put quotes around text that contains a colon and a space.",
    unterminatedQuote: "A quoted string that starts here is never closed.",
    unterminatedFlow: "A bracket or brace that starts here is never closed.",
    flowExpected: "Expected {expected} here.",
    duplicateKey: "The key “{key}” is used twice (first on line {first}).",
    unknownAlias: "No anchor named “{name}” is defined above this alias.",
    badEscape:
      "Unknown escape in a double-quoted string: a backslash followed by “{char}”.",
    badCharacter:
      "The character “{char}” cannot start a plain value. Put the text in quotes.",
    complexKey:
      "Complex keys (a list or mapping used as a key) are not supported.",
    unexpectedContent:
      "Unexpected content here. Check the indentation, and that each line belongs to the mapping or list above it.",
    badTag: "The tag {tag} does not fit the value “{text}”.",
    badBlockHeader:
      "“{text}” is not a valid block-scalar header. Use a bar or a greater-than sign with an optional digit and plus or minus.",
    mergeNotMap:
      "The value of a merge key must be a mapping or a list of mappings.",
    unsupportedDirective: "The directive {text} is not supported.",
    tooLarge:
      "The result would be longer than {limit} characters. Aliases can expand a small file a long way.",
    tooDeep: "The nesting is too deep to convert.",
    internal: "Something unexpected stopped the conversion.",
    jsonUnexpectedToken: "Unexpected character “{char}”.",
    jsonUnexpectedEnd:
      "The JSON ends too early. Check for a missing closing bracket, brace or quote.",
    jsonTrailingComma:
      "A comma before a closing bracket or brace is not allowed in JSON.",
    jsonExpected: "Expected {expected} here.",
    jsonSingleQuote: "JSON strings use double quotes, not single quotes.",
    jsonUnquotedKey: "JSON keys must be in double quotes.",
    jsonComment: "JSON does not allow comments.",
    jsonLiteral: "{text} is not valid JSON. Use null, true, false or a number.",
    jsonUnterminatedString: "A string that starts here is never closed.",
    jsonControlChar:
      "A line break or control character inside a string must be written as an escape sequence.",
    jsonBadEscape:
      "Unknown escape in a string: a backslash followed by “{char}”.",
    jsonBadNumber:
      "This is not a valid JSON number (no leading zeros, trailing dot or plus sign).",
    jsonExtraData: "There is extra text after the end of the JSON value.",
    jsonTooDeep: "The JSON is nested more than 500 levels deep.",
  },
  warnings: {
    yaml11:
      "Line {line}: “{value}” is text here (YAML 1.2). Older YAML 1.1 parsers read it as a boolean.",
    nonFinite:
      "Line {line}: “{value}” has no JSON form and was written as null.",
    unknownTag: "Line {line}: the tag {tag} is not known here and was ignored.",
    propsOnKey:
      "Line {line}: an anchor or tag written before a key was ignored.",
    duplicateKey:
      "Line {line}: the key “{key}” appears more than once; the last value is used.",
  },
} as const;

type Strings = typeof YAML_JSON_CONVERTER_STRINGS;
type MessageCode = keyof Strings["messages"];
type WarningCode = keyof Strings["warnings"];

const JSON_INDENTS: readonly JsonIndent[] = ["2", "4", "tab", "compact"];
const YAML_INDENTS = ["2", "4"] as const;

function describeProblem(problem: Problem, strings: Strings): string {
  const known = Object.hasOwn(strings.messages, problem.code);
  const template = known
    ? strings.messages[problem.code as MessageCode]
    : strings.messages.internal;
  return t(template, problem.params);
}

function describeWarning(
  warning: {
    code: string;
    line: number;
    params: Readonly<Record<string, string | number>>;
  },
  strings: Strings
): string {
  const template = Object.hasOwn(strings.warnings, warning.code)
    ? strings.warnings[warning.code as WarningCode]
    : "";
  return t(template, { ...warning.params, line: warning.line });
}

export default function YamlJsonConverterWidget() {
  const strings = useToolUi(YAML_JSON_CONVERTER_STRINGS);
  const [direction, setDirection] = useState<Direction>("yamlToJson");
  const [source, setSource] = useState("");
  const [options, setOptions] = useState<ConvertOptions>(
    DEFAULT_CONVERT_OPTIONS
  );
  const deferred = useDeferredValue(source);
  let outcome: Outcome | null = null;
  if (deferred.trim() !== "") {
    outcome =
      direction === "yamlToJson"
        ? yamlToJson(deferred, options)
        : jsonToYaml(deferred, options);
  }
  const yamlSide = direction === "yamlToJson";

  return (
    <section className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="yjc-mode"
        onChange={(value) => {
          setDirection(value);
          setSource("");
        }}
        options={[
          { value: "yamlToJson", label: strings.modes.yamlToJson },
          { value: "jsonToYaml", label: strings.modes.jsonToYaml },
        ]}
        value={direction}
      />
      <TextAreaField
        hint={strings.hint}
        id="yjc-input"
        label={yamlSide ? strings.inputLabelYaml : strings.inputLabelJson}
        onChange={setSource}
        placeholder={
          yamlSide ? strings.placeholderYaml : strings.placeholderJson
        }
        rows={12}
        value={source}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() =>
            setSource(yamlSide ? strings.exampleYaml : strings.exampleJson)
          }
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource("")}
          type="button"
        >
          {strings.clear}
        </button>
        <button
          className={BUTTON_SMALL}
          disabled={outcome === null || !outcome.ok}
          onClick={() => {
            if (outcome?.ok) {
              setSource(outcome.text);
              setDirection(yamlSide ? "jsonToYaml" : "yamlToJson");
            }
          }}
          type="button"
        >
          {strings.useAsInput}
        </button>
      </div>
      <OptionsPanel
        onChange={setOptions}
        options={options}
        strings={strings}
        yamlSide={yamlSide}
      />
      <div aria-live="polite" className="space-y-4">
        {outcome === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <Results outcome={outcome} strings={strings} yamlSide={yamlSide} />
        )}
      </div>
    </section>
  );
}

function OptionsPanel({
  options,
  onChange,
  strings,
  yamlSide,
}: {
  options: ConvertOptions;
  onChange: (next: ConvertOptions) => void;
  strings: Strings;
  yamlSide: boolean;
}) {
  const set = <K extends keyof ConvertOptions>(
    key: K,
    value: ConvertOptions[K]
  ) => onChange({ ...options, [key]: value });
  return (
    <Panel title={strings.optionsHeading}>
      {yamlSide ? (
        <>
          <SelectField
            id="yjc-json-indent"
            label={strings.jsonIndentLabel}
            onChange={(value) => set("jsonIndent", value)}
            options={JSON_INDENTS.map((value) => ({
              value,
              label: strings.jsonIndents[value],
            }))}
            value={options.jsonIndent}
          />
          <Checkbox
            checked={options.merge}
            label={strings.merge}
            onChange={(value) => set("merge", value)}
          />
        </>
      ) : (
        <>
          <SelectField
            id="yjc-yaml-indent"
            label={strings.yamlIndentLabel}
            onChange={(value) => set("yamlIndent", value === "4" ? 4 : 2)}
            options={YAML_INDENTS.map((value) => ({
              value,
              label: strings.yamlIndents[value],
            }))}
            value={String(options.yamlIndent) as (typeof YAML_INDENTS)[number]}
          />
          <Checkbox
            checked={options.blockScalars}
            label={strings.blockScalars}
            onChange={(value) => set("blockScalars", value)}
          />
        </>
      )}
    </Panel>
  );
}

function Results({
  outcome,
  strings,
  yamlSide,
}: {
  outcome: Outcome;
  strings: Strings;
  yamlSide: boolean;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  if (!outcome.ok) {
    return <ProblemView problem={outcome.problem} strings={strings} />;
  }
  const { summary } = outcome;
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <Notice>{outcome.empty ? strings.emptyDocument : strings.valid}</Notice>
      {outcome.documents > 1 ? (
        <Notice>
          {tPlural(strings.documentsNote, locale, outcome.documents, {
            count: fmt(outcome.documents),
          }).trim()}
        </Notice>
      ) : null}
      <StatGrid>
        <StatCard
          emphasis
          label={strings.typeLabel}
          value={strings.types[summary.type]}
        />
        <StatCard label={strings.entries} value={fmt(summary.entries)} />
        <StatCard label={strings.depth} value={fmt(summary.depth)} />
        <StatCard label={strings.keys} value={fmt(summary.keys)} />
      </StatGrid>
      <div className="space-y-2">
        <label className={LABEL} htmlFor="yjc-output">
          {yamlSide ? strings.outputLabelJson : strings.outputLabelYaml}
        </label>
        <textarea
          className={TEXTAREA}
          id="yjc-output"
          readOnly
          rows={Math.min(24, outcome.text.split("\n").length + 1)}
          spellCheck={false}
          value={outcome.text}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={outcome.text}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            downloadText(
              yamlSide ? strings.filenameJson : strings.filenameYaml,
              outcome.text,
              yamlSide ? "application/json" : "application/yaml"
            )
          }
          type="button"
        >
          {strings.download}
        </button>
      </div>
      {outcome.warnings.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.warningsHeading}</h3>
          <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
            {outcome.warnings.slice(0, 12).map((warning) => (
              <li
                key={`${warning.code}-${warning.line}-${JSON.stringify(warning.params)}`}
              >
                {describeWarning(warning, strings)}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}

function ProblemView({
  problem,
  strings,
}: {
  problem: Problem;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const place =
    problem.line > 0
      ? t(strings.problemAt, {
          line: fmt(problem.line),
          column: fmt(problem.column),
        })
      : strings.problemNoLine;
  const caret = " ".repeat(Math.max(0, Math.min(problem.column - 1, 119)));
  return (
    <div className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.problemHeading}</h2>
      <ErrorNotice>
        {place} — {describeProblem(problem, strings)}
      </ErrorNotice>
      {problem.excerpt === "" ? null : (
        <pre className={`${TEXTAREA} min-h-0 overflow-x-auto`}>
          {`${problem.excerpt}\n${caret}^`}
        </pre>
      )}
    </div>
  );
}
